import { useAtom } from "jotai";
import apiClient from "./apiClient";
import { runsAtom } from "../atoms/runAtoms";
import { Run } from "@quantizr-engineering/common/types/run";
import { FIVE_MINUTES_MS } from "../utils/consts";
import { useEffect } from "react";
import { toast } from "sonner";

export const useRunService = () => {
  const [runs, setRuns] = useAtom(runsAtom);

  useEffect(() => {
    cacheGetRuns();
  }, []);

  const getRuns = async () => {
    setRuns((prev) => ({ ...prev, loading: true }));
    try {
      const response = await apiClient.get("/run");
      setRuns((prev) => ({
        loading: false,
        data: response.data ?? [],
        lastFetched: Date.now(),
      }));
      return response;
    } catch (error) {
      setRuns((prev) => ({
        ...prev,
        loading: false,
        data: [],
        lastFetched: null,
      }));
      throw error;
    }
  };

  const cacheGetRuns = async () => {
    if (!!runs.loading) {
      return;
    }
    if (!runs.lastFetched || Date.now() - runs.lastFetched > FIVE_MINUTES_MS) {
      await getRuns();
    }
  };

  const postRun = async (run: Run) => {
    try {
      const response = await apiClient.post("/run", run, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      toast.success("Run saved successfully!");
      await getRuns();
      return response;
    } catch (error) {
      toast.error("Failed to post run.");
      throw error;
    }
  };

  const generateRunForecastReport = async (
    run: Run,
    email: string
  ) => {
    try {
      const response = await apiClient.post(
        `/run/${run.id}/report/forecast`,
        {
          emailRecipient: email,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      toast.success("Report Sent!");
      return response;
    } catch (error) {
      toast.error("Failed to generate forecast report");
      throw error;
    }
  };

  const patchRun = async (run: Run) => {
    try {
      const response = await apiClient.patch(`/run/${run.id}`, run, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      await getRuns();
      return response;
    } catch (error) {
      toast.error("Failed to update run");
      throw error;
    }
  };

  const copyRun = async (run: Run) => {
    try {
      const response = await apiClient.post(
        `/run/${run.id}/duplicate`,
        {
          name: `${run.name} (copy)`,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      );
      await getRuns();
      return response;
    } catch (error) {
      toast.error("Failed to copy run");
      throw error;
    }
  };

  const deleteRun = async (run: Run) => {
    try {
      const response = await apiClient.delete(`/run/${run.id}`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      });
      await getRuns();
      return response;
    } catch (error) {
      toast.error("Failed to delete run");
      throw error;
    }
  };

  return {
    runs: runs.data,
    runsLoading: runs.loading,
    getRuns,
    cacheGetRuns,
    postRun,
    generateRunForecastReport,
    patchRun,
    deleteRun,
    copyRun,
  };
};
